import { Condition, RuleResult, RuleType } from './types';

/**
 * Any action should not be allowed if user detail is not known yet
 */
const noActionWithoutAuthentication: RuleType = {
    name: 'noActionWithoutAuthentication',
    priority: 110,
    resolve: ({ userCondition: { isUserLoaded } }: Condition) => {
        if (!isUserLoaded) {
            return RuleResult.DENY;
        }
    },
};

/**
 * Any action should not be allowed after class end time.
 */
const actionOpenTimeConstraint: RuleType = {
    name: 'actionOpenTimeConstraint',
    priority: 100,
    resolve: ({ courseCondition: { isBeforeClassEndTime } }: Condition) => {
        if (!isBeforeClassEndTime) {
            return RuleResult.DENY;
        }
    },
};

/**
 * No action allowed if course is not active
 */
const noActionIfCourseIsNotActive: RuleType = {
    name: 'noActionIfCourseIsNotActive',
    priority: 100,
    resolve: ({ courseCondition: { isCourseActive } }: Condition) => {
        if (!isCourseActive) {
            return RuleResult.DENY;
        }
    },
};

/**
 * Non-partner user cannot do any action on public unlisted class
 * Special case: https://tasks.awstcprojects.com/browse/PMT-7079 users not in PARTNER_ORG should not see the enroll button for a course that has no customers (public partner unlisted)
 */
const nonPartnerUserCanDoNoActionOnPartnerPublicUnlisted: RuleType = {
    name: 'nonPartnerUserCanDoNoActionOnPartnerPublicUnlisted',
    priority: 95,
    resolve: ({ courseCondition: { isPartnerPublicUnlistedClass }, userCondition: { isPartnerUser } }: Condition) => {
        if (isPartnerPublicUnlistedClass && !isPartnerUser) {
            return RuleResult.DENY;
        }
    },
};

/**
 * Rejects team LearningActivity enrollment if the user is not associated with teamIds which are associated with Learning Activity
 */
const nonTeamLearnerCanEnrollLearningActivityAssociatedWithTeam: RuleType = {
    name: 'nonTeamLearnerCanEnrollLearningActivityAssociatedWithTeam',
    priority: 95,
    resolve: ({
        courseCondition: { teamIds },
        userCondition: { learnerTeamIds },
        featureFlagsCondition: { isTeamAuthorizationEnabled },
    }: Condition) => {
        if (!!teamIds && teamIds.length > 0 && isTeamAuthorizationEnabled) {
            if (!learnerTeamIds || !learnerTeamIds.some((learnerTeamId) => teamIds.includes(learnerTeamId))) {
                return RuleResult.DENY;
            }
        }
    },
};

/**
 * Partner Public Unlisted case
 * Partner user can action on courses without organization specification
 */
const allowsActionsIfPartnerPublicUnlistedClassAndOrgListEmptyAndUserIsPartnerUser: RuleType = {
    name: 'allowsActionsIfPartnerPublicUnlistedClassAndOrgListEmptyAndUserIsPartnerUser',
    priority: 90,
    resolve: (condition: Condition) => {
        const {
            courseCondition: { orgIds, isPartnerPublicUnlistedClass },
            userCondition: { isPartnerUser },
        } = condition;
        if (orgIds && orgIds.length === 0 && isPartnerUser && isPartnerPublicUnlistedClass) {
            return RuleResult.ALLOW;
        }
    },
};

/**
 * B2B (Partner & Customer) user case where the orgId matches
 * Amazonian case where the orgId matches (Internal Private class)
 */
const allowsActionsIfOrgListContainsUserOrg: RuleType = {
    name: 'allowsActionsIfOrgListContainsUserOrg',
    priority: 90,
    resolve: (condition: Condition) => {
        const {
            courseCondition: { orgIds },
            userCondition: { userOrganizationId },
        } = condition;
        if (orgIds && userOrganizationId && orgIds.includes(userOrganizationId)) {
            return RuleResult.ALLOW;
        }
    },
};

/**
 * B2I user case
 * Allows B2I user to request access to a course
 */
const allowsActionsForB2IUser: RuleType = {
    name: 'allowsActionsForB2IUser',
    priority: 90,
    resolve: (condition: Condition) => {
        const {
            userCondition: { isCustomerB2IUser, isPartnersB2IUser, requestingAccessToOrgId },
        } = condition;
        if ((isCustomerB2IUser || isPartnersB2IUser) && requestingAccessToOrgId) {
            return RuleResult.ALLOW_REQUEST_ACCESS;
        }
    },
};

/**
 * Explicitly denies any action if no rule met
 */
const noActionIfNoRuleMet: RuleType = {
    name: 'noActionIfNoRuleMet',
    priority: 1,
    resolve: () => {
        return RuleResult.DENY;
    },
};

export const rules = [
    noActionWithoutAuthentication, // 110
    actionOpenTimeConstraint, // 100
    noActionIfCourseIsNotActive, // 100
    nonPartnerUserCanDoNoActionOnPartnerPublicUnlisted, // 95
    nonTeamLearnerCanEnrollLearningActivityAssociatedWithTeam, // 95
    allowsActionsIfPartnerPublicUnlistedClassAndOrgListEmptyAndUserIsPartnerUser, // 90
    allowsActionsIfOrgListContainsUserOrg, // 90
    allowsActionsForB2IUser, // 90
    noActionIfNoRuleMet, // 1
];

export const queryRegistrationRules = [
    noActionWithoutAuthentication,
    noActionIfCourseIsNotActive,
    nonPartnerUserCanDoNoActionOnPartnerPublicUnlisted,
    nonTeamLearnerCanEnrollLearningActivityAssociatedWithTeam,
    allowsActionsIfPartnerPublicUnlistedClassAndOrgListEmptyAndUserIsPartnerUser,
    allowsActionsIfOrgListContainsUserOrg,
    noActionIfNoRuleMet,
];
